/**
 * Realtime providers — wrap a screen to keep it live.
 *
 * Each one subscribes to the channel of one scope and refreshes, through the
 * shared refresh map, whatever the events arriving there make stale. Mount the
 * provider around the screen that shows that scope's data:
 *
 * ```tsx
 * <OrderRealtimeProvider orderId={orderId}>
 *   <OrderDetail />
 * </OrderRealtimeProvider>
 * ```
 *
 * Side effects beyond refreshing (a toast, a dialog, a typing indicator) are not
 * a provider's job: the feature that owns them listens with `useRealtimeRelay`.
 */

import { useGetAuthMe } from "@birdwatch/client";
import {
  canSubscribeToStaffChannels,
  REALTIME_CAMPAIGN_CHANNEL,
  REALTIME_TEAM_CHANNEL,
  realtimeUserChannel,
} from "@birdwatch/common";
import type { ReactNode } from "react";

import { useRealtimeRefresh } from "./use-realtime-refresh";

type ProviderProps = { children?: ReactNode };

/** One order: its detail, visits, quotes, scope, notes, conversation, AI panels. Channel = `orderId`. */
export function OrderRealtimeProvider({
  orderId,
  children,
}: ProviderProps & { orderId: string | null | undefined }) {
  useRealtimeRefresh({ channels: [orderId] });
  return <>{children}</>;
}

/** One home: its record, household, spaces, list items, requests, orders. Channel = `placeId`. */
export function PlaceRealtimeProvider({
  placeId,
  children,
}: ProviderProps & { placeId: string | null | undefined }) {
  useRealtimeRefresh({ channels: [placeId] });
  return <>{children}</>;
}

/** One billing account: payment methods, subscriptions, memberships, wallet. Channel = `accountId`. */
export function AccountRealtimeProvider({
  accountId,
  children,
}: ProviderProps & { accountId: string | null | undefined }) {
  useRealtimeRefresh({ channels: [accountId] });
  return <>{children}</>;
}

/**
 * Every member campaign: an admin's add, edit or delete changes what every member is shown
 * (the Home modal and tile, the campaign links). Channel = `REALTIME_CAMPAIGN_CHANNEL`, open to
 * any signed-in user.
 */
export function CampaignRealtimeProvider({ children }: ProviderProps) {
  useRealtimeRefresh({ channels: [REALTIME_CAMPAIGN_CHANNEL] });
  return <>{children}</>;
}

/** The signed-in user's own channel: profile, roles, calendar, onboarding, views. */
export function UserRealtimeProvider({ children }: ProviderProps) {
  const { data: me } = useGetAuthMe().get();
  useRealtimeRefresh({
    channels: [me?.id ? realtimeUserChannel(me.id) : null],
  });
  return <>{children}</>;
}

/**
 * The team channel: every list-level change the office and admin show. Staff
 * only — the relay refuses anyone else, and a refused channel drops the tab's
 * whole shared stream, so it is requested only for a staff role.
 */
export function TeamRealtimeProvider({ children }: ProviderProps) {
  const { data: me } = useGetAuthMe().get();
  const isStaff = canSubscribeToStaffChannels(me?.roles);
  useRealtimeRefresh({
    channels: [isStaff ? REALTIME_TEAM_CHANNEL : null],
    enabled: isStaff,
  });
  return <>{children}</>;
}
